import { type FC, forwardRef, useContext } from 'react';
import { CheckCircle } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMe } from '@/hooks/use-me';
import { formatDate } from '@/utils/format/date';
import { formatNumber } from '@/utils/format/number';
import { PenaltyDescriptionLabel } from '@/warranty/domain/entities/penalty-description-id';
import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import { WarrantyDetailsContext } from '../../pages/warranty-details';
import { GetPenaltyOverview } from '../../queries/get-penalty-overview';
import { TableSkeleton } from '../skeletons/TableSkeleton';
import { TooltipAdditionalInfo } from '../tooltip-additional-info';

export const ZeroPenaltyTableStateRow: FC = () => {
  return (
    <tr className="border border-green-100 bg-green-50 text-green-800 shadow-inner">
      <td colSpan={6} className="p-6 text-center align-middle">
        <div className="flex flex-col items-center justify-center gap-2">
          <CheckCircle className="size-6 text-green-600" />
          <div className="text-lg font-semibold">
            No penalty records detected
          </div>
          <div className="text-sm text-gray-600">
            All systems are operating within warranty conditions.
          </div>
        </div>
      </td>
    </tr>
  );
};

export type PenaltyTableProps = {
  filters: IWarrantyRepository.Filters;
};
export const PenaltyOverviewTable = forwardRef<
  HTMLDivElement,
  PenaltyTableProps
>(function ({ filters }, ref) {
  const asset = useContext(WarrantyDetailsContext);
  const me = useMe();

  // get warranty result from dummy repository
  const penaltyOverview = GetPenaltyOverview.useQuery({
    filters,
    assetFullFrontendId: asset!.fullFrontendId,
    assetId: asset!.id,
    customerId: me.config.id,
  });

  if (penaltyOverview.isLoading) return <TableSkeleton columns={6} rows={1} />;
  if (penaltyOverview.isError)
    return <div>Error loading Warranty overview</div>;

  const hasAnyPenalties = penaltyOverview.data?.some(
    (entry) => entry.sohPenalty > 0,
  );

  if (!penaltyOverview.data || !hasAnyPenalties) {
    return (
      <div>
        <Table
          striped
          dense
          bleed
          ref={ref}
          className="w-full table-auto overflow-x-auto"
        >
          <TableHead className="sticky top-0 bg-gray-100">
            <TableRow>
              <TableHeader className="text-center">Description</TableHeader>
              <TableHeader className="text-center">Extreme value</TableHeader>
              <TableHeader className="text-center">Latest Detect.</TableHeader>
              <TableHeader className="text-center">Duration</TableHeader>
              <TableHeader className="text-center">Total Count</TableHeader>
              <TableHeader className="text-center">SOH Penalty</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            <ZeroPenaltyTableStateRow />
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div>
      <Table
        striped
        dense
        bleed
        ref={ref}
        className="w-full table-auto overflow-x-auto"
      >
        <TableHead className="sticky top-0 bg-gray-100">
          <TableRow>
            <TableHeader className="text-center">Description</TableHeader>
            <TableHeader className="text-center">Extreme value</TableHeader>
            <TableHeader className="text-center">Latest Detect.</TableHeader>
            <TableHeader className="text-center">Duration</TableHeader>
            <TableHeader className="text-center">Total Count</TableHeader>
            <TableHeader className="text-center">
              <TooltipAdditionalInfo content={<>Excluding predictions</>}>
                SOH Penalty
              </TooltipAdditionalInfo>
            </TableHeader>
          </TableRow>
        </TableHead>

        <TableBody>
          {penaltyOverview.data
            .filter((penaltyEntry) => penaltyEntry.sohPenalty > 0)
            .map((penaltyEntry) => (
              <TableRow key={penaltyEntry.id} className="hover:bg-gray-50">
                <TableCell justify="left">
                  {PenaltyDescriptionLabel[penaltyEntry.description]}
                </TableCell>
                <TableCell className="whitespace-nowrap" justify="center">
                  {`${formatNumber(penaltyEntry.extremeValue, 1)} ${penaltyEntry.unit}`}
                </TableCell>
                <TableCell justify="center">
                  {penaltyEntry.latestDetect.getTime() === 0
                    ? 'No data'
                    : formatDate(penaltyEntry.latestDetect)}
                </TableCell>
                <TableCell className="whitespace-nowrap" justify="center">
                  {formatNumber(penaltyEntry.duration, 1)} h
                </TableCell>
                <TableCell justify="center">
                  {penaltyEntry.totalCount}
                </TableCell>
                <TableCell justify="center">
                  {formatNumber(penaltyEntry.sohPenalty, 2)}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
});
PenaltyOverviewTable.displayName = 'PenaltyOverviewTable';
